// Section KPIs dynamiques pour la page Rentabilité
import { type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { TrendingUp, TrendingDown, DollarSign, Percent, Building2, AlertTriangle, Info, ChevronRight } from 'lucide-react';
import { KPIs } from '@/hooks/useChantiersFiltresRentabilite';
import { cn } from '@/lib/utils';

// Types de tri/filtre possibles
export type KpiSortType = 'chantiers' | 'revenus' | 'couts' | 'marge' | 'rentabilite' | 'en_perte' | null;

interface Props {
  kpis: KPIs;
  filtreLabel: string;
  loading?: boolean;
  /** Callback quand un KPI est cliqué */
  onKpiClick?: (sortType: KpiSortType) => void;
  /** KPI actuellement actif (pour l'indicateur visuel) */
  activeKpi?: KpiSortType;
}

export function KPIsSection({ kpis, filtreLabel, loading, onKpiClick, activeKpi }: Props) {
  const formatMontant = (n: number) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  const isClickable = !!onKpiClick;

  const cards: Array<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: typeof Building2;
    color: string;
    tooltip: string;
    sortType: KpiSortType;
    extraContent?: ReactNode;
  }> = [
    {
      title: 'Chantiers',
      value: kpis.nombreChantiers,
      subtitle: filtreLabel,
      icon: Building2,
      color: 'text-blue-500',
      tooltip: 'Cliquez pour réinitialiser le tri et voir tous les chantiers.',
      sortType: 'chantiers',
    },
    {
      title: 'Revenus totaux',
      value: formatMontant(kpis.totalRevenus),
      icon: DollarSign,
      color: 'text-green-500',
      tooltip: 'Cliquez pour trier par revenus décroissants.',
      sortType: 'revenus',
    },
    {
      title: 'Coûts totaux',
      value: formatMontant(kpis.totalCouts),
      icon: TrendingDown,
      color: 'text-orange-500',
      tooltip: 'Cliquez pour trier par coûts décroissants.',
      sortType: 'couts',
    },
    {
      title: 'Marge globale',
      value: formatMontant(kpis.margeGlobale),
      icon: kpis.margeGlobale >= 0 ? TrendingUp : TrendingDown,
      color: kpis.margeGlobale >= 0 ? 'text-emerald-500' : 'text-red-500',
      tooltip: 'Cliquez pour trier par marge décroissante.',
      sortType: 'marge',
    },
    {
      title: 'Rentabilité moyenne',
      value: `${kpis.rentabiliteMoyenne.toFixed(1)}%`,
      icon: Percent,
      color: kpis.rentabiliteMoyenne >= 10 ? 'text-green-500' : 'text-amber-500',
      tooltip: 'Cliquez pour trier par taux de rentabilité décroissant.',
      sortType: 'rentabilite',
      extraContent: kpis.nombreChantiers > 0 ? (() => {
        const count = kpis.chantiersAuDessusObjectif;
        const total = kpis.nombreChantiers;
        const allAbove = count === total;
        const noneAbove = count === 0;
        return (
          <span className={cn("text-xs font-medium",
            allAbove ? "text-green-500" : noneAbove ? "text-red-500" : "text-orange-500"
          )}>
            {allAbove ? '✅' : noneAbove ? '🔴' : '⚠️'} {count}/{total} ≥ 18%
          </span>
        );
      })() : undefined,
    },
    {
      title: 'En perte',
      value: kpis.chantiersEnPerte,
      subtitle: 'chantier(s)',
      icon: AlertTriangle,
      color: kpis.chantiersEnPerte > 0 ? 'text-red-500' : 'text-gray-400',
      tooltip: 'Cliquez pour filtrer uniquement les chantiers en perte (marge négative).',
      sortType: 'en_perte',
    },
  ];

  // Masquer le KPI "En perte" quand aucun chantier n'est en perte
  const visibleCards = cards.filter(card => {
    if (card.sortType === 'en_perte' && kpis.chantiersEnPerte === 0) return false;
    return true;
  });

  return (
    <div className={cn(
      "grid gap-4 md:grid-cols-2 lg:grid-cols-3",
      visibleCards.length === 6 ? "xl:grid-cols-3 2xl:grid-cols-6" : "xl:grid-cols-3 2xl:grid-cols-5"
    )}>
      {visibleCards.map((card, i) => {
        const Icon = card.icon;
        const isActive = activeKpi === card.sortType;

        return (
          <Card
            key={i}
            className={cn(
              'relative',
              loading && 'animate-pulse',
              isClickable && 'cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg',
              isActive && 'ring-2 ring-primary shadow-lg'
            )}
            onClick={() => isClickable && onKpiClick?.(card.sortType)}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-1 pt-3 px-4">
              <CardTitle className="text-xs font-medium text-white uppercase">
                {card.title}
              </CardTitle>
              <Icon className={`h-4 w-4 ${card.title === 'Chantiers' ? 'text-white' : card.color}`} />
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <div className={`text-xl font-black ${card.title === 'Chantiers' ? 'text-white' : card.color}`}>
                {loading ? '...' : card.value}
              </div>
              {card.subtitle && (
                <p className="text-xs text-muted-foreground">{card.subtitle}</p>
              )}
              {card.extraContent}
            </CardContent>
            {/* Indicateur cliquable (chevron) */}
            {isClickable && (
              <div className={cn(
                "absolute bottom-3 right-10 p-1 rounded-full transition-colors",
                isActive ? "bg-primary/20" : "bg-primary/10 group-hover:bg-primary/20"
              )}>
                <ChevronRight className={cn(
                  "h-3.5 w-3.5 text-primary transition-transform",
                  isActive && "translate-x-0.5"
                )} />
              </div>
            )}
            {/* Tooltip info */}
            <HoverCard openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  className="absolute bottom-3 right-3 p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
                <p className="text-sm text-foreground">{card.tooltip}</p>
              </HoverCardContent>
            </HoverCard>
          </Card>
        );
      })}
    </div>
  );
}
